import apiClient from './api';

// --- Type Definitions ---

// Type for response from /info/symbols
export type AvailableSymbolsResponse = string[];

// Type for response from /info/price/{symbol}
export type MarkPriceResponse = number;

// Type for response from /info/orderbook/{symbol}
export interface OrderbookData {
    best_bid: number | null;
    best_ask: number | null;
    spread: number;
    timestamp: number;
}

/**
 * Defines the structure for precision details of a symbol.
 */
interface SymbolPrecisionDetail {
    price_decimals: number;
    size_decimals: number;
}

/**
 * Defines the response structure for the precisions endpoint.
 * It's a dictionary mapping symbol strings to their precision details.
 */
export type SymbolPrecisionsResponse = {
    [symbol: string]: SymbolPrecisionDetail;
};


// --- Service Object ---

export const infoService = {
    /**
     * Fetch the list of all available trading symbols.
     * @returns Promise resolving to an array of symbol strings.
     */
    getAvailableSymbols: async (): Promise<AvailableSymbolsResponse> => {
        try {
            console.log('infoService.getAvailableSymbols: Starting API call to /info/symbols');
            console.log('API base URL:', apiClient.defaults.baseURL);
            
            const response = await apiClient.get<AvailableSymbolsResponse>('/info/symbols');
            
            console.log('infoService.getAvailableSymbols: Success!', {
                status: response.status,
                dataLength: response.data.length,
                firstFewSymbols: response.data.slice(0, 5)
            });
            
            return response.data;
        } catch (error: any) {
            console.error('infoService.getAvailableSymbols: API call failed', {
                error,
                message: error?.message,
                status: error?.response?.status,
                statusText: error?.response?.statusText,
                baseURL: apiClient.defaults.baseURL
            });
            throw new Error('Failed to fetch available symbols');
        }
    },

    /**
     * Get the current mark price for a specific symbol.
     * @param symbol - The symbol to fetch the price for.
     * @returns Promise resolving to the mark price number.
     */
    getMarkPrice: async (symbol: string): Promise<MarkPriceResponse> => {
        try {
            const response = await apiClient.get<MarkPriceResponse>(`/info/price/${symbol}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching mark price for ${symbol}:`, error);
            throw new Error(`Failed to fetch mark price for ${symbol}`);
        }
    },

    /**
     * Get best bid/ask data for a specific symbol.
     * @param symbol - The symbol to fetch data for.
     * @returns Promise resolving to an object with best bid/ask.
     */
    getOrderbookData: async (symbol: string): Promise<OrderbookData> => {
        try {
            const response = await apiClient.get<OrderbookData>(`/info/orderbook/${symbol}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching orderbook data:', error);
            throw new Error('Failed to fetch orderbook data');
        }
    },

    /**
     * Fetches the price and size precision for all available symbols.
     * @returns Promise resolving to a dictionary mapping symbols to their precision.
     */
    getSymbolPrecisions: async (): Promise<SymbolPrecisionsResponse> => {
        try {
            console.log('infoService.getSymbolPrecisions: Starting API call to /info/precisions');
            const response = await apiClient.get<SymbolPrecisionsResponse>('/info/precisions');
            console.log('infoService.getSymbolPrecisions: Success!', {
                status: response.status,
                precisionCount: Object.keys(response.data).length
            });
            return response.data;
        } catch (error: any) {
            console.error('infoService.getSymbolPrecisions: API call failed', {
                error,
                message: error?.message,
                status: error?.response?.status,
                statusText: error?.response?.statusText
            });
            throw new Error('Failed to fetch symbol precisions');
        }
    },
}; 